import type { Road } from '@/types/road';
import { findClosestRoad } from '../utils/roadDetection';
import { Coordinates } from '../../../../types';
import { getPreferredRoadName } from '../../../../utils/roadUtils';
import { calculatePointToLineSegmentDistance } from '@/utils/geolocation/distanceCalculation';

export const roadNameParser = {
  /**
   * Calculate distance between coordinates and a road
   */
  calculateDistanceToRoad: (coordinates: Coordinates, road: Road): number => {
    if (!road.geometry || !road.geometry.coordinates) {
      return Infinity;
    }

    let minDistance = Infinity;

    try {
      if (road.geometry.type === 'LineString') {
        // Single line
        const lineCoords = road.geometry.coordinates as [number, number][];
        for (let i = 0; i < lineCoords.length - 1; i++) {
          const segmentStart = {
            lng: lineCoords[i]?.[0] ?? 0,
            lat: lineCoords[i]?.[1] ?? 0,
          };
          const segmentEnd = {
            lng: lineCoords[i + 1]?.[0] ?? 0,
            lat: lineCoords[i + 1]?.[1] ?? 0,
          };
          const distance = calculatePointToLineSegmentDistance(
            coordinates,
            segmentStart,
            segmentEnd
          );
          minDistance = Math.min(minDistance, distance);
        }
      } else if (road.geometry.type === 'MultiLineString') {
        // Multiple lines
        const multiLineCoords = road.geometry.coordinates as unknown as [
          number,
          number,
        ][][];
        for (const line of multiLineCoords) {
          for (let i = 0; i < line.length - 1; i++) {
            const segmentStart = {
              lng: line[i]?.[0] ?? 0,
              lat: line[i]?.[1] ?? 0,
            };
            const segmentEnd = {
              lng: line[i + 1]?.[0] ?? 0,
              lat: line[i + 1]?.[1] ?? 0,
            };
            const distance = calculatePointToLineSegmentDistance(
              coordinates,
              segmentStart,
              segmentEnd
            );
            minDistance = Math.min(minDistance, distance);
          }
        }
      }
    } catch {
      return Infinity;
    }

    return minDistance;
  },

  /**
   * Find the closest road to given coordinates
   */
  findClosestRoadWithDistance: (
    coordinates: Coordinates,
    roads: Road[]
  ): { road: Road; distance: number } | null => {
    const closestRoad = findClosestRoad(coordinates, roads);
    if (!closestRoad) {
      return null;
    }

    const distance = roadNameParser.calculateDistanceToRoad(
      coordinates,
      closestRoad
    );
    return { road: closestRoad, distance };
  },

  /**
   * Get preferred road name for display
   * Ensures proper formatting for GPS suggestions
   */
  getPreferredRoadName,

  /**
   * Parse road reference from various formats
   */
  parseRoadReference: (
    ref: string
  ): { type: string; number: string } | null => {
    if (!ref) return null;

    // Motorways: A1, A2, A4
    const motorwayMatch = ref.match(/^A(\d+)$/i);
    if (motorwayMatch && motorwayMatch[1]) {
      return { type: 'motorway', number: motorwayMatch[1] };
    }

    // Expressways: S1-S99
    const expresswayMatch = ref.match(/^S(\d+)$/i);
    if (expresswayMatch && expresswayMatch[1]) {
      return { type: 'expressway', number: expresswayMatch[1] };
    }

    // National roads: DK1-DK94
    const nationalMatch = ref.match(/^DK(\d+)$/i);
    if (nationalMatch && nationalMatch[1]) {
      return { type: 'national', number: nationalMatch[1] };
    }

    // Voivodeship roads: DW100-DW999
    const voivodeshipMatch = ref.match(/^DW(\d+)$/i);
    if (voivodeshipMatch && voivodeshipMatch[1]) {
      return { type: 'voivodeship', number: voivodeshipMatch[1] };
    }

    return null;
  },
};
